<script setup lang="ts">
import type { FavoriteButtonProps } from './types'
import { LucideBookmark } from 'lucide-vue-next'

const props = withDefaults(defineProps<FavoriteButtonProps>(), {
  variant: 'outline'
})

// Favorite functionality
const { isFavorite, toggleFavorite: toggleFav } = useFavorites()
const isFav = computed(() => isFavorite.value(props.id))

const handleToggleFavorite = async (event: Event) => {
  event.preventDefault()
  event.stopPropagation()

  try {
    await toggleFav(props.id)
  } catch (error) {
    console.error('Error toggling favorite:', error)
  }
}

const { t } = useI18n()

// Compute button classes based on variant
const buttonClasses = computed(() => {
  const baseClasses =
    'flex items-center justify-center rounded-full transition-all duration-200 hover:scale-110 active:scale-95'

  if (props.variant === 'outline') {
    return [
      baseClasses,
      'h-10 w-10 shrink-0 border border-gray-200 bg-white hover:border-gray-300',
      isFav.value ? 'border-blue-200 text-blue-600' : 'text-gray-400'
    ]
  } else {
    // card variant
    return [
      baseClasses,
      'absolute right-3 top-3 z-10 h-8 w-8 bg-white/90 backdrop-blur-sm hover:bg-white hover:shadow-lg',
      isFav.value ? 'text-blue-600 shadow-md' : 'text-gray-400'
    ]
  }
})

const iconClasses = computed(() => {
  const baseClasses = 'transition-all duration-200'
  const sizeClass = props.variant === 'outline' ? 'h-5 w-5' : 'h-4 w-4'
  const fillClass = isFav.value ? 'fill-current' : ''

  return [baseClasses, sizeClass, fillClass]
})
</script>

<template>
  <ClientOnly>
    <button
      :class="[buttonClasses, props.class]"
      :title="
        isFav
          ? t('widget.news_page.details.remove_from_favorites')
          : t('widget.news_page.details.add_to_favorites')
      "
      :aria-label="
        isFav
          ? t('widget.news_page.details.remove_from_favorites')
          : t('widget.news_page.details.add_to_favorites')
      "
      @click="handleToggleFavorite"
    >
      <LucideBookmark :class="iconClasses" />
    </button>
  </ClientOnly>
</template>
